import React, { createContext, useContext, ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { getDemoSessionToken } from '../utils/demoSession';

const DEMO_USER_EMAIL = 'demo-visitor@buildahead.demo';
const DEMO_USER_NAME = 'Demo Visitor';

interface AuthContextType {
  isAuthenticated: boolean;
  account: AccountInfo | null;
  userEmail: string | null;
  userName: string | null;
  login: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const account = accounts[0] || null;

  // Demo mode has no MSAL account at all — every screen that reads userEmail/
  // isAuthenticated from this context (Dashboard, useWeeklyData, Ask, Report, Upload…)
  // would otherwise silently no-op its data fetch on `if (!userEmail) return`. Rather
  // than patching each of those call sites individually, the one synthetic identity
  // lives here — everything downstream keeps working unmodified.
  const isDemoMode = !!getDemoSessionToken();
  const isAuthenticated = msalIsAuthenticated || isDemoMode;
  const userEmail = isDemoMode ? DEMO_USER_EMAIL : (account?.username || null);
  const userName = isDemoMode ? DEMO_USER_NAME : (account?.name || null);

  const login = async () => {
    await instance.loginRedirect(loginRequest);
  };

  const logout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, account, userEmail, userName, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
